/*!
 * OutIn Fino PDP — Add to Cart / cart-drawer integration.
 *
 * Before this file: the Fino product page's Add to Cart button lived inside
 * a plain native <form id="fino-product-form" action="/cart/add">, so
 * clicking it did a full native form submission -> full page navigation to
 * /cart. The sticky mobile buy bar (sections/fino-sticky-buy.liquid) has no
 * add-to-cart logic of its own at all — its button just calls
 * document.getElementById('fino-product-form').requestSubmit(), i.e. it
 * submits this exact same form. So both surfaces inherited the same
 * "bounce to /cart" behaviour from one shared form, and fixing that form
 * fixes both at once.
 *
 * This file intercepts that one form's submit and performs an AJAX
 * /cart/add.js call, then dispatches the theme's real CartLinesUpdateEvent
 * (the same event class @theme/product-form.js and
 * assets/outin-home-quick-add.js already dispatch) so the *existing*,
 * unmodified cart-drawer-component / cart-items-component (rendered once,
 * globally, in layout/theme.liquid) pick it up, refresh their contents and
 * auto-open — exactly like the homepage Hot Selling "Quick Add" already
 * does. No cart-drawer markup, rendering or open/close logic is
 * reimplemented here; this only supplies the "add + notify" glue.
 *
 * Left untouched by this file:
 *  - Buy Now (a separate type="button" in sections/fino-main-product.liquid,
 *    handled entirely by its own fetch()+redirect in
 *    assets/fino-product-page.js).
 *  - Homepage / Hot Selling Quick Add (assets/outin-home-quick-add.js) — a
 *    fully separate component with its own dialog and product data.
 *  - Variant swatch selection and quantity stepper logic (still owned by
 *    assets/fino-product-page.js) — this file only reads the form's
 *    current values at submit time.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const FORM_ID = 'fino-product-form';

/**
 * @param {HTMLFormElement} form
 */
async function handleFinoAddToCart(form) {
  const addBtn = form.querySelector('[data-fino-add]');
  const variantInput = form.querySelector('[data-fino-variant-id]');
  const qtyInput = form.querySelector('[data-fino-qty]');
  const variantId = variantInput ? variantInput.value : new FormData(form).get('id');
  const quantity = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);

  if (!variantId) return;

  // Reuses the same inline-error presentation already used by the Buy Now
  // button on this page (assets/fino-product-page.js), so errors stay
  // visible in a style consistent with the rest of the page rather than
  // introducing a second error UI pattern.
  let errorEl = form.querySelector('[data-fino-add-error]');
  if (!errorEl) {
    errorEl = document.createElement('p');
    errorEl.setAttribute('data-fino-add-error', '');
    errorEl.setAttribute('role', 'alert');
    errorEl.style.cssText = 'margin:.75rem 0 0;color:#f7b4a9;font-size:.85rem;font-weight:700;';
    const buttons = form.querySelector('.fino-buybtns');
    if (buttons) buttons.insertAdjacentElement('afterend', errorEl);
    else form.appendChild(errorEl);
  }
  errorEl.textContent = '';
  errorEl.hidden = true;

  if (addBtn) {
    addBtn.disabled = true;
    addBtn.setAttribute('aria-busy', 'true');
  }

  try {
    // Same section list Quick Add gathers: the global cart drawer plus any
    // other cart-items-component instances present on the page.
    const sectionIds = Array.from(
      new Set([
        'cart-drawer-section',
        ...Array.from(
          document.querySelectorAll('cart-items-component[data-section-id]'),
          (item) => item.dataset.sectionId
        ),
      ])
    ).slice(0, 5);

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: sectionIds,
        sections_url: window.location.pathname + window.location.search,
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description || result.message || 'Unable to add this item.');
    }

    const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!cartResponse.ok) throw new Error('Unable to add this item.');
    const cart = await cartResponse.json();

    // Defensive fallback (mirrors outin-home-quick-add.js): fetch the
    // drawer section markup directly if /cart/add.js didn't include it.
    if (!result.sections || !result.sections['cart-drawer-section']) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', 'cart-drawer-section');
      const sectionsResponse = await fetch(url, { cache: 'no-store' });
      if (sectionsResponse.ok) {
        result.sections = { ...result.sections, ...(await sectionsResponse.json()) };
      }
    }

    const settled = Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        source: 'fino-product-form',
        sourceId: FORM_ID,
        itemCount: quantity,
        items: cart.items,
        sections: result.sections,
        didError: false,
      },
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity }],
        promise: settled,
      })
    );

    // Belt-and-braces open, same as Quick Add — works regardless of the
    // auto_open_cart_drawer theme setting, and regardless of whether the
    // submit came from the main buy box or the sticky bar's requestSubmit().
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  } catch (error) {
    errorEl.textContent = (error && error.message) || 'Unable to add this item. Please try again.';
    errorEl.hidden = false;
  } finally {
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  // NOTE: this form has a hidden <input name="id" ...> (the variant id field),
  // which DOM-clobbers HTMLFormElement's own `id` IDL property (HTMLFormElement
  // is [OverrideBuiltins], so a same-named form control shadows it) — `form.id`
  // resolves to that <input> element here, not the string "fino-product-form".
  // Read the id content attribute directly instead, which clobbering can't touch.
  if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== FORM_ID) return;
  event.preventDefault();
  handleFinoAddToCart(form);
});
